/** @jsx jsx */
import React, { useCallback, useMemo, useRef } from 'react';
import { token } from '@atlaskit/tokens';
import { css, jsx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import Automation from '@atlaskit/icon/core/automation';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import {
	GLOBAL_TRANSITION_PORT,
	GLOBAL_TRANSITION_PORT_OFFSET,
	STATE_COLOURS,
	TRANSITION_STROKE_WIDTH,
} from './ellipsis.tsx';
import { EDGE_TYPE, STATUS_GLOBAL_TRANSITIONS_TYPE } from './directionKeys.tsx';
import type { Point, StatefulDiagramGlobalTransition } from './toPortInfo.tsx';
import type { ExpandableItemReference, ItemReference } from './ListEdgeDetails.tsx';
import { DiagramArrow } from './DiagramArrow.tsx';
import { truncateLabel } from './isEveryPropertyDefined.tsx';
import {
	GLOBAL_TRANSITION_ROUTE_HEIGHT_DISTANCE as ROUTE_HEIGHT,
	globalTransitionRoute,
} from './globalTransitionRoute.tsx';
import { useStrokeColor } from './useStrokeColor.tsx';
import useLayoutWidth from './useLayoutWidth.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';

const anyTransitionLabelWithCount = defineMessage({
	id: 'accessible-workflow-diagram.diagram.global-transition.any-transition-label-with-count',
	defaultMessage: 'Any ({count})',
	description:
		'Message showing "Any" transition label with the count of global transitions to this status.',
});

const iconLabelHasRules = defineMessage({
	id: 'accessible-workflow-diagram.diagram.global-transition.icon-label-has-rules',
	defaultMessage: 'Has workflow rules',
	description:
		'Accessible label for automation icon indicating the transition has workflow rules configured.',
});

const iconLabelApproved = defineMessage({
	id: 'accessible-workflow-diagram.diagram.global-transition.icon-label-approved',
	defaultMessage: 'Used when approval is approved',
	description:
		'Accessible label for a green check icon shown on a workflow transition, indicating this transition runs when an approver approves the issue.',
});

const iconLabelApprovedInactive = defineMessage({
	id: 'accessible-workflow-diagram.diagram.global-transition.icon-label-approved-inactive',
	defaultMessage: 'Used when approval is approved (inactive)',
	description:
		'Accessible label for a disabled/grey check icon shown on a workflow transition, indicating this transition runs when an approver approves the issue (inactive).',
});

const iconLabelDeclined = defineMessage({
	id: 'accessible-workflow-diagram.diagram.global-transition.icon-label-declined',
	defaultMessage: 'Used when approval is declined',
	description:
		'Accessible label for a red cross icon shown on a workflow transition, indicating this transition runs when an approver declines the issue.',
});

const iconLabelDeclinedInactive = defineMessage({
	id: 'accessible-workflow-diagram.diagram.global-transition.icon-label-declined-inactive',
	defaultMessage: 'Used when approval is declined (inactive)',
	description:
		'Accessible label for a disabled/grey cross icon shown on a workflow transition, indicating this transition runs when an approver declines the issue (inactive).',
});

const MIN_LOZENGE_WIDTH = 40;
const LOZENGE_HEIGHT = 24;
const LOZENGE_BORDER_RADIUS = LOZENGE_HEIGHT / 2;
const FOCUS_RING_WIDTH = 4;
const LOZENGE_PADDING = 10; // total padding around left and right of lozenge, L + R value

const renderTransitionIcon = (
	icon: { type: string },
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): React.JSX.Element | null => {
	const { type } = icon;

	switch (type) {
		case 'bolt':
			return <Automation label={formatMessage(iconLabelHasRules)} />;
		case 'check':
			return (
				<StatusSuccessIcon
					label={formatMessage(iconLabelApproved)}
					color={token('color.icon.success')}
				/>
			);
		case 'check_inactive':
			return (
				<StatusSuccessIcon
					label={formatMessage(iconLabelApprovedInactive)}
					color={token('color.icon.disabled')}
				/>
			);
		case 'cross':
			return (
				<CrossCircleIcon
					label={formatMessage(iconLabelDeclined)}
					color={token('color.icon.danger')}
				/>
			);
		case 'cross_inactive':
			return (
				<CrossCircleIcon
					label={formatMessage(iconLabelDeclinedInactive)}
					color={token('color.icon.disabled')}
				/>
			);
		default:
			return null;
	}
};

export interface Props {
	statusId: string;
	portPosition: Point | undefined;
	transitions: StatefulDiagramGlobalTransition[];
	expandedItem?: ExpandableItemReference;
	targetedItem?: ItemReference;
	hoveredItem?: ItemReference;
	onExpandedChange?: (item: ExpandableItemReference | undefined) => void;
	onSelect?: (item: ItemReference | undefined) => void;
	onHover?: (item: ItemReference | undefined) => void;
	isImmutable?: boolean;
}

const GlobalTransitionMulti = ({
	statusId,
	portPosition,
	transitions,
	isImmutable,
	expandedItem,
	targetedItem,
	hoveredItem,
	onExpandedChange,
	onSelect,
	onHover,
}: Props): React.JSX.Element | null => {
	const isCurrentlyHovered =
		hoveredItem?.type === STATUS_GLOBAL_TRANSITIONS_TYPE && statusId === hoveredItem.id;
	const hasSelectedTransition = useMemo(
		() => transitions.some((transition) => transition.isSelected),
		[transitions],
	);
	const isHighlighted = useMemo(
		() => transitions.some((transition) => transition.isHighlighted),
		[transitions],
	);

	const { formatMessage } = useIntl();
	const stroke = useStrokeColor({
		flag: undefined,
		isHighlighted,
		isSelected: hasSelectedTransition,
		isHovered: isCurrentlyHovered,
	});

	const textRef = useRef<SVGTextElement | null>(null);
	const textWidth = useLayoutWidth(textRef);

	const handleMenuToggle = useCallback(
		({ isOpen }: { isOpen: boolean }) => {
			if (isImmutable) {
				return;
			}
			if (isOpen) {
				onExpandedChange?.({ type: STATUS_GLOBAL_TRANSITIONS_TYPE, id: statusId });
			} else {
				onExpandedChange?.(undefined);
			}
		},
		[isImmutable, onExpandedChange, statusId],
	);

	const onMouseEnter = useCallback(() => {
		if (isImmutable) {
			return;
		}
		onHover?.({ type: STATUS_GLOBAL_TRANSITIONS_TYPE, id: statusId });
	}, [isImmutable, onHover, statusId]);

	const onMouseLeave = useCallback(() => {
		if (isImmutable) {
			return;
		}
		onHover?.(undefined);
	}, [isImmutable, onHover]);

	if (!portPosition) {
		return null;
	}

	const isExpanded =
		expandedItem?.type === STATUS_GLOBAL_TRANSITIONS_TYPE && statusId === expandedItem.id;
	const isMenuOpen = isExpanded && !isImmutable;
	const isTargeted =
		targetedItem?.type === STATUS_GLOBAL_TRANSITIONS_TYPE && statusId === targetedItem.id;
	const lozengeWidth = Math.max(MIN_LOZENGE_WIDTH, textWidth + LOZENGE_PADDING);
	const routeWidth = lozengeWidth / 2;
	const path = globalTransitionRoute(portPosition, GLOBAL_TRANSITION_PORT_OFFSET, lozengeWidth / 2);
	const textX = portPosition.x + (lozengeWidth - textWidth) / 2;
	const textY = portPosition.y - ROUTE_HEIGHT - 8;
	const lozengeX = portPosition.x + routeWidth - lozengeWidth / 2;
	const lozengeY = portPosition.y - ROUTE_HEIGHT - LOZENGE_HEIGHT;

	return (
		<g onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
			<path fill="none" stroke="transparent" d={path} strokeWidth="15" />
			{/* Transition line */}
			<path fill="none" stroke={stroke} strokeLinejoin="round" d={path} strokeWidth="2" />
			<DiagramArrow
				stroke={stroke}
				portIndex={GLOBAL_TRANSITION_PORT}
				portPosition={portPosition}
				portOffset={GLOBAL_TRANSITION_PORT_OFFSET}
			/>
			{isTargeted && (
				<rect
					x={lozengeX}
					y={lozengeY}
					width={lozengeWidth}
					height={LOZENGE_HEIGHT}
					rx={LOZENGE_BORDER_RADIUS}
					ry={LOZENGE_BORDER_RADIUS}
					stroke={STATE_COLOURS.focused}
					strokeWidth={FOCUS_RING_WIDTH * 2}
				/>
			)}
			{/* Lozenge bg */}
			<rect
				x={lozengeX}
				y={lozengeY}
				width={lozengeWidth}
				height={LOZENGE_HEIGHT}
				rx={LOZENGE_BORDER_RADIUS}
				ry={LOZENGE_BORDER_RADIUS}
				fill={token('color.background.neutral.bold')}
				strokeWidth={TRANSITION_STROKE_WIDTH}
				stroke={hasSelectedTransition || isExpanded ? stroke : undefined}
			/>
			{/* Lozenge text */}
			<text
				ref={textRef}
				textAnchor="start"
				fontSize="11px"
				fill={token('color.text.inverse')}
				x={textX}
				y={textY}
				aria-hidden="true"
			>
				<tspan>
					{truncateLabel(formatMessage(anyTransitionLabelWithCount, { count: transitions.length }))}
				</tspan>
			</text>
			<foreignObject x={lozengeX} y={lozengeY} width={lozengeWidth} height={LOZENGE_HEIGHT}>
				<DropdownMenu<HTMLDivElement>
					trigger={({ triggerRef, isSelected: isDropdownOpen, testId, ...providedProps }) => (
						<div
							{...providedProps}
							ref={triggerRef}
							css={buttonStyles}
							aria-hidden="true"
							data-testid={testId}
						/>
					)}
					placement="right-start"
					isOpen={isMenuOpen}
					onOpenChange={handleMenuToggle}
					testId="accessible-workflow-diagram.global-transitions-menu"
					{...(expVal('hix-9182_sprt_a11y_global_transition', 'isEnabled', false) && {
						autoFocus: true,
					})}
				>
					<DropdownItemGroup>
						{transitions.map((transition) => {
							return (
								<DropdownItem
									key={transition.id}
									isSelected={transition.isSelected}
									onClick={() => onSelect?.({ id: transition.id, type: EDGE_TYPE })}
									elemAfter={
										transition.icons.length > 0 ? (
											<div css={iconWrapperStyles}>
												{transition.icons.map((icon) => (
													<React.Fragment key={icon.type}>
														{renderTransitionIcon(icon, formatMessage)}
													</React.Fragment>
												))}
											</div>
										) : undefined
									}
								>
									{transition.label}
								</DropdownItem>
							);
						})}
					</DropdownItemGroup>
				</DropdownMenu>
			</foreignObject>
		</g>
	);
};

const buttonStyles = css({
	width: '100%',
	height: '100%',
	opacity: 0,
});

const iconWrapperStyles = css({
	display: 'flex',
	gap: token('space.050'),
	alignItems: 'center',
	flexShrink: 0,
	minWidth: 'fit-content',
	overflow: 'visible',
	width: 'max-content',
	marginLeft: token('space.100'),
});

export default GlobalTransitionMulti;
